import React, { useEffect, useRef, useState, useCallback } from 'react';
import './SiteDetails.css';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale } from 'chart.js';
import 'chartjs-adapter-date-fns'; // Import the date adapter
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDaysInMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay } from 'date-fns'; // Import date-fns functions
import { api as axios } from '../utils/apiClient';
import jsPDF from 'jspdf';
import vajraLogo from '../assets/Vajra single logo.png';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale // Register the Time Scale
);

const Uptime = ({ site }) => {
  const chartRef = useRef(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedRange, setSelectedRange] = useState('24hours'); // 30minutes, 24hours, month, 3months, custom
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD format
  const [uptimeData, setUptimeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dailyUptime, setDailyUptime] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [loadingDailyUptime, setLoadingDailyUptime] = useState(false);
  const [loadingIncidents, setLoadingIncidents] = useState(false);

  // Fetch uptime history data based on selected range or date
  const fetchUptimeHistory = useCallback(async (range, date = null) => {
    if (!site?._id) return;
    
    setLoading(true);
    try {
      let url = `/api/sites/${site._id}/uptime-history?range=${range}`;
      if (date) {
        url += `&date=${date}`;
      }
      const response = await axios.get(url);
      console.log(`[Uptime] Fetched data for range ${range}${date ? `, date ${date}` : ''}:`, response.data);
      
      // Log diagnostic information
      if (response.data.diagnostic) {
        console.log(`[Uptime] Diagnostic Info:`, {
          totalEntries: response.data.diagnostic.totalEntries,
          filteredEntries: response.data.diagnostic.filteredEntries,
          criticalEntries: response.data.diagnostic.criticalEntries,
          healthyEntries: response.data.diagnostic.healthyEntries,
          warningEntries: response.data.diagnostic.warningEntries,
          downtimeSeconds: response.data.downtimeSeconds,
          period: `${response.data.diagnostic.periodStart} to ${response.data.diagnostic.periodEnd}`
        });
      }
      
      setUptimeData(response.data);
    } catch (error) {
      console.error('Error fetching uptime history:', error);
      // Fallback: For 24hours range, try to use site's statusHistory directly
      if (range === '24hours' && site?.statusHistory && site.statusHistory.length > 0) {
        const now = Date.now();
        const last24Hours = now - 24 * 60 * 60 * 1000;
        const filtered = site.statusHistory
          .filter(entry => new Date(entry.timestamp).getTime() >= last24Hours)
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        if (filtered.length > 0) {
          // Calculate basic stats
          const healthyCount = filtered.filter(e => e.status === 'Healthy' || e.status === 'Warning').length;
          const criticalCount = filtered.filter(e => e.status === 'Critical').length;
          const uptimePercent = (healthyCount / filtered.length) * 100;
          const downtimePercent = (criticalCount / filtered.length) * 100;
          
          setUptimeData({
            range: '24hours',
            data: filtered.map(entry => ({
              timestamp: entry.timestamp,
              status: entry.status,
              responseTime: entry.responseTime
            })),
            uptimePercentage: Math.round(uptimePercent * 10) / 10,
            downtimePercentage: Math.round(downtimePercent * 10) / 10,
            totalChecks: filtered.length,
            healthyChecks: healthyCount,
            downtimeSeconds: 0, // Approximate
            timeUnit: 'hour'
          });
        } else {
          setUptimeData(null);
        }
      } else {
        setUptimeData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [site?._id, site?.statusHistory]);

  // Fetch daily uptime data for Historical Uptime calendar
  const fetchDailyUptime = useCallback(async () => {
    if (!site?._id) {
      console.log('[Uptime] No site ID, skipping daily uptime fetch');
      return;
    }
    
    setLoadingDailyUptime(true);
    try {
      console.log(`[Uptime] Fetching daily uptime for site ${site._id}`);
      const response = await axios.get(`/api/sites/${site._id}/daily-uptime`);
      console.log('[Uptime] Daily uptime response:', response.data);
      const dailyData = response.data.dailyUptime || [];
      console.log(`[Uptime] Setting daily uptime data: ${dailyData.length} days`);
      setDailyUptime(dailyData);
    } catch (error) {
      console.error('Error fetching daily uptime:', error);
      console.error('Error details:', error.response?.data || error.message);
      setDailyUptime([]);
    } finally {
      setLoadingDailyUptime(false);
    }
  }, [site?._id]);

  // Fetch incidents history
  const fetchIncidents = useCallback(async () => {
    if (!site?._id) {
      console.log('[Uptime] No site ID, skipping incidents fetch');
      return;
    }
    
    setLoadingIncidents(true);
    try {
      console.log(`[Uptime] Fetching incidents for site ${site._id}`);
      const response = await axios.get(`/api/sites/${site._id}/incidents`);
      console.log('[Uptime] Incidents response:', response.data);
      const incidentsData = response.data.incidents || [];
      console.log(`[Uptime] Setting incidents data: ${incidentsData.length} incidents`);
      setIncidents(incidentsData);
    } catch (error) {
      console.error('Error fetching incidents:', error);
      console.error('Error details:', error.response?.data || error.message);
      setIncidents([]);
    } finally {
      setLoadingIncidents(false);
    }
  }, [site?._id]);

  // Fetch data when range or date changes or site changes
  useEffect(() => {
    // If custom is selected, use 'custom' range and pass the date parameter if available
    const rangeToUse = selectedRange === 'custom' ? 'custom' : selectedRange;
    const dateToUse = selectedRange === 'custom' ? selectedDate : null;
    fetchUptimeHistory(rangeToUse, dateToUse);
  }, [selectedRange, selectedDate, fetchUptimeHistory]);

  // Fetch daily uptime and incidents when site changes
  useEffect(() => {
    fetchDailyUptime();
    fetchIncidents();
  }, [site?._id, fetchDailyUptime, fetchIncidents]);

  // Handle date selection
  const handleDateChange = (e) => {
    const date = e.target.value;
    if (date) {
      setSelectedDate(date);
      setSelectedRange('custom'); // Set to custom when date is selected
    } else {
      setSelectedDate(null);
    }
  };

  // Handle range change - clear date if switching to non-custom range
  const handleRangeChange = (e) => {
    const newRange = e.target.value;
    setSelectedRange(newRange);
    if (newRange !== 'custom') {
      setSelectedDate(null);
    }
  };


  // Helper function to format response time
  const formatResponseTime = (ms) => {
    if (ms === undefined || ms === null) return 'N/A';
    return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2)}s`;
  };

  // Get range display name
  const getRangeDisplayName = (range) => {
    switch (range) {
      case '30minutes': return 'Last 30 minutes';
      case '24hours': return 'Last 24 hours';
      case 'month': return 'Last month';
      case '3months': return 'Last 3 months';
      case 'custom': return 'Custom';
      default: return 'Last 24 hours';
    }
  };

  // Get time unit for X-axis
  const getTimeUnit = () => {
    if (!uptimeData) return 'hour';
    return uptimeData.timeUnit || 'hour';
  };

  // Get X-axis title
  const getXAxisTitle = () => {
    if (selectedDate) {
      return `Time (${format(new Date(selectedDate + 'T00:00:00'), 'yyyy-MM-dd')})`;
    }
    const rangeNames = {
      '30minutes': 'Time (Last 30 Minutes)',
      '24hours': 'Time (Last 24 Hours)',
      'month': 'Time (Last Month)',
      '3months': 'Time (Last 3 Months)',
      'custom': 'Time (Custom Range)'
    };
    return rangeNames[selectedRange] || 'Time';
  };

  // Prepare data for the chart
  const prepareChartData = () => {
    if (!uptimeData || !uptimeData.data || uptimeData.data.length === 0) {
      console.log('[Uptime] No data available for chart');
      return null;
    }

    console.log('[Uptime] Preparing chart data from:', uptimeData.data.length, 'entries');

    // Process and validate data
    const validData = uptimeData.data
      .map(entry => {
        const timestamp = entry.timestamp ? new Date(entry.timestamp) : null;
        if (!timestamp || isNaN(timestamp.getTime())) {
          console.warn('[Uptime] Invalid timestamp:', entry.timestamp);
          return null;
        }
        return {
          timestamp,
          status: entry.status || 'Unknown',
          responseTime: entry.responseTime !== null && entry.responseTime !== undefined ? entry.responseTime : null
        };
      })
      .filter(entry => entry !== null)
      .sort((a, b) => a.timestamp - b.timestamp);

    if (validData.length === 0) {
      console.log('[Uptime] No valid data after processing');
      return null;
    }

    console.log('[Uptime] Chart data prepared with', validData.length, 'valid entries');

    return {
      labels: validData.map(entry => entry.timestamp),
      datasets: [
        {
          label: 'Response time',
          data: validData.map(entry => entry.responseTime),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          tension: 0.1,
          yAxisID: 'y',
          spanGaps: true,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
        {
          label: 'Uptime',
          data: validData.map(entry => ({ 
            x: entry.timestamp, 
            y: entry.status === 'Healthy' || entry.status === 'Warning' ? 1 : 0 
          })),
          borderColor: 'rgb(75, 192, 75)',
          backgroundColor: 'rgba(75, 192, 75, 0.1)',
          pointBackgroundColor: validData.map(entry => {
            if (entry.status === 'Healthy') return 'rgb(75, 192, 75)';
            if (entry.status === 'Warning') return 'rgb(255, 193, 7)';
            return 'rgb(255, 99, 132)';
          }),
          showLine: true,
          stepped: true,
          yAxisID: 'y1',
          pointRadius: 2,
          pointHoverRadius: 4,
        },
        {
          label: 'Downtime',
          data: validData.map(entry => ({ 
            x: entry.timestamp, 
            y: entry.status === 'Critical' ? 1 : null 
          })),
          borderColor: 'rgb(220, 53, 69)',
          backgroundColor: 'rgba(220, 53, 69, 0.25)',
          pointBackgroundColor: validData.map(entry => 
            entry.status === 'Critical' ? 'rgb(220, 53, 69)' : 'transparent'
          ),
          pointBorderColor: validData.map(entry => 
            entry.status === 'Critical' ? 'rgb(220, 53, 69)' : 'transparent'
          ),
          showLine: true,
          stepped: true,
          yAxisID: 'y1',
          pointRadius: validData.map(entry => 
            entry.status === 'Critical' ? 5 : 0
          ),
          pointHoverRadius: 7,
          fill: 'origin',
          spanGaps: false,
        },
      ],
    };
  };

  const chartData = prepareChartData();

  // Get chart options based on selected range
  const getChartOptions = () => {
    const timeUnit = getTimeUnit();
    const xAxisTitle = getXAxisTitle();
    
    // Determine display format based on time unit and whether date is selected
    let displayFormat, tooltipFormat, unit;
    if (selectedDate) {
      // For specific date, show hours and minutes
      displayFormat = 'HH:mm';
      tooltipFormat = 'yyyy-MM-dd HH:mm:ss';
      unit = 'hour';
    } else {
      switch (timeUnit) {
        case 'minute':
          displayFormat = 'HH:mm';
          tooltipFormat = 'yyyy-MM-dd, HH:mm:ss';
          unit = 'minute';
          break;
        case 'hour':
          displayFormat = 'h a';
          tooltipFormat = 'yyyy-MM-dd, h:mm a';
          unit = 'hour';
          break;
        case 'day':
          displayFormat = 'MMM d';
          tooltipFormat = 'yyyy-MM-dd';
          unit = 'day';
          break;
        case 'week':
          displayFormat = 'MMM d';
          tooltipFormat = 'yyyy-MM-dd';
          unit = 'week';
          break;
        default:
          displayFormat = 'h a';
          tooltipFormat = 'yyyy-MM-dd, h:mm a';
          unit = 'hour';
      }
    }

    return {
      responsive: true,
      maintainAspectRatio: false, // Allow chart to fill container
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: selectedDate 
            ? `Uptime History (${format(new Date(selectedDate + 'T00:00:00'), 'yyyy-MM-dd')})`
            : `Uptime History (${getRangeDisplayName(selectedRange)})`,
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: function(context) {
              const date = new Date(context[0].label);
              return format(date, tooltipFormat);
            },
            label: function(context) {
              let label = context.dataset.label || '';
              if (label === 'Response time') {
                label += ': ' + formatResponseTime(context.raw);
              } else if (label === 'Uptime') {
                const value = typeof context.raw === 'object' ? context.raw.y : context.raw;
                label += ': ' + (value === 1 ? 'Healthy' : 'Critical/Warning');
              } else if (label === 'Downtime') {
                const value = typeof context.raw === 'object' ? context.raw.y : context.raw;
                label += ': ' + (value === 1 ? 'Down (Critical)' : 'Up');
              }
              return label;
            }
          }
        }
      },
      scales: {
        x: {
          type: 'time', // Use time scale for chronological data
          time: {
            unit: unit,
            tooltipFormat: tooltipFormat,
            displayFormats: {
              [unit]: displayFormat
            }
          },
          ticks: {
            maxTicksLimit: selectedRange === '3months' ? 12 : selectedRange === 'month' ? 30 : selectedRange === '24hours' ? 24 : selectedRange === '30minutes' ? 6 : 24,
            major: {
              enabled: true
            }
          },
          title: {
            display: true,
            text: xAxisTitle
          }
        },
        y: {
          type: 'linear', // Linear scale for response time
          display: true,
          position: 'left',
          title: {
            display: true,
            text: 'Response Time (ms)'
          },
          beginAtZero: true
        },
        y1: {
          type: 'linear', // Linear scale for uptime status
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Status'
          },
          grid: {
            drawOnChartArea: false, // Only draw grid lines for the left axis
          },
          ticks: {
            stepSize: 1,
            callback: function(value, index, values) {
              if (value === 1) return 'Healthy';
              if (value === 0) return 'Down';
              return '';
            }
          },
          min: -0.1,
          max: 1.1
        },
      },
    };
  };

  // Function to manually trigger uptime check
  const handleRefreshUptime = async () => {
    setIsRefreshing(true);
    try {
      await axios.post(`/api/sites/${site._id}/check-uptime`);
      // Refetch the current range/date data
      const rangeToUse = selectedRange === 'custom' ? 'custom' : selectedRange;
      const dateToUse = selectedRange === 'custom' ? selectedDate : null;
      await fetchUptimeHistory(rangeToUse, dateToUse);
    } catch (error) {
      console.error('Error refreshing uptime:', error);
      alert('Failed to refresh uptime data');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Helper function to add logo to all pages except cover (page 1)
  // skipCoverPage: if true, skip page 1 (for reports with cover page)
  //                if false, add logo to all pages including page 1 (for regular reports)
  const addLogoToAllPages = async (doc, pageWidth, pageHeight, skipCoverPage = false) => {
    try {
      // Load the logo image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Logo loading timeout'));
        }, 5000);
        
        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = (err) => {
          clearTimeout(timeout);
          console.error('Logo image error:', err);
          reject(err);
        };
        
        img.src = vajraLogo;
      });
      
      // Logo dimensions - small size for top right corner
      const logoHeight = 8; // mm - small size
      const logoAspectRatio = img.width / img.height;
      const logoWidth = logoHeight * logoAspectRatio;
      
      // Position at top right corner with small margin
      const logoMargin = 5; // mm from edges
      const logoX = pageWidth - logoWidth - logoMargin;
      const logoY = logoMargin;
      
      // Get total pages
      const totalPages = doc.internal.pages.length - 1;
      
      // Add logo to pages - skip page 1 if skipCoverPage is true
      const startPage = skipCoverPage ? 2 : 1;
      for (let i = startPage; i <= totalPages; i++) {
        doc.setPage(i);
        doc.addImage(vajraLogo, 'PNG', logoX, logoY, logoWidth, logoHeight);
      }
    } catch (error) {
      console.error('Error adding logo to PDF pages:', error);
      // Continue without logo if loading fails
    }
  };

  // Function to download uptime/downtime log data as PDF
  const handleDownloadPDF = async () => {
    if (!site?.statusHistory || site.statusHistory.length === 0) {
      alert('No log data available to download');
      return;
    }

    // Get all raw log entries for the selected range
    const now = Date.now();
    let hoursBack;
    switch (selectedRange) {
      case '30minutes':
        hoursBack = 0.5;
        break;
      case '24hours':
        hoursBack = 24;
        break;
      case 'month':
        hoursBack = 24 * 30;
        break;
      case '3months':
        hoursBack = 24 * 90;
        break;
      case 'custom':
        hoursBack = 24; // Default to 24 hours for custom
        break;
      default:
        hoursBack = 24;
    }

    // Calculate period start time based on range or selected date
    let periodStartTime;
    if (selectedRange === 'custom' && selectedDate) {
      // For custom with date, use the selected date
      const selectedDateObj = new Date(selectedDate + 'T00:00:00.000Z');
      periodStartTime = selectedDateObj.getTime();
    } else {
      periodStartTime = now - hoursBack * 60 * 60 * 1000;
    }
    
    const filteredLogs = site.statusHistory
      .filter(entry => {
        const entryTime = new Date(entry.timestamp).getTime();
        if (selectedRange === 'custom' && selectedDate) {
          // For custom date, filter within that specific day
          const periodEndTime = periodStartTime + (24 * 60 * 60 * 1000) - 1;
          return entryTime >= periodStartTime && entryTime <= periodEndTime;
        } else {
          return entryTime >= periodStartTime;
        }
      })
      .map(entry => ({
        timestamp: new Date(entry.timestamp),
        status: entry.status || 'Unknown',
        responseTime: entry.responseTime
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    if (filteredLogs.length === 0) {
      alert('No log data available for the selected period');
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const lineHeight = 7;
    let yPosition = margin;
    const maxY = pageHeight - margin;

    // Helper function to add a new page if needed
    const checkNewPage = (requiredSpace) => {
      if (yPosition + requiredSpace > maxY) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Uptime & Downtime Log Report', margin, yPosition);
    yPosition += lineHeight * 2;

    // Site Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Site: ${site.name || site.url}`, margin, yPosition);
    yPosition += lineHeight;
    doc.text(`URL: ${site.url}`, margin, yPosition);
    yPosition += lineHeight;
    doc.text(`Report Period: ${getRangeDisplayName(selectedRange)}`, margin, yPosition);
    yPosition += lineHeight;
    doc.text(`Date Range: ${format(new Date(periodStartTime), 'yyyy-MM-dd HH:mm:ss')} to ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`, margin, yPosition);
    yPosition += lineHeight;
    doc.text(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`, margin, yPosition);
    yPosition += lineHeight * 2;

    // Summary Statistics
    checkNewPage(lineHeight * 6);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary Statistics', margin, yPosition);
    yPosition += lineHeight * 1.5;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const totalLogs = filteredLogs.length;
    const healthyLogs = filteredLogs.filter(e => e.status === 'Healthy').length;
    const warningLogs = filteredLogs.filter(e => e.status === 'Warning').length;
    const criticalLogs = filteredLogs.filter(e => e.status === 'Critical').length;
    
    doc.text(`Total Log Entries: ${totalLogs}`, margin, yPosition);
    yPosition += lineHeight;
    doc.text(`Healthy: ${healthyLogs} (${totalLogs > 0 ? Math.round((healthyLogs / totalLogs) * 100) : 0}%)`, margin, yPosition);
    yPosition += lineHeight;
    doc.text(`Warning: ${warningLogs} (${totalLogs > 0 ? Math.round((warningLogs / totalLogs) * 100) : 0}%)`, margin, yPosition);
    yPosition += lineHeight;
    doc.text(`Critical: ${criticalLogs} (${totalLogs > 0 ? Math.round((criticalLogs / totalLogs) * 100) : 0}%)`, margin, yPosition);
    yPosition += lineHeight;
    
    if (uptimeData) {
      doc.text(`Uptime Percentage: ${uptimePercentage !== null ? `${uptimePercentage}%` : 'N/A'}`, margin, yPosition);
      yPosition += lineHeight;
      doc.text(`Downtime Percentage: ${downtimePercentage !== null ? `${downtimePercentage}%` : 'N/A'}`, margin, yPosition);
      yPosition += lineHeight;
      doc.text(`Total Downtime: ${formatTime(uptimeData.downtimeSeconds || 0)}`, margin, yPosition);
      yPosition += lineHeight;
    }
    yPosition += lineHeight;

    // Log Data Table Header
    checkNewPage(lineHeight * 2);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Detailed Log Data', margin, yPosition);
    yPosition += lineHeight * 1.5;

    // Table Headers
    checkNewPage(lineHeight * 2);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const col1 = margin;
    const col2 = margin + 55;
    const col3 = margin + 95;
    const col4 = margin + 135;
    
    doc.text('Timestamp', col1, yPosition);
    doc.text('Status', col2, yPosition);
    doc.text('Response Time', col3, yPosition);
    doc.text('Duration', col4, yPosition);
    yPosition += lineHeight;
    
    // Draw line under header
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition - 2, pageWidth - margin, yPosition - 2);
    yPosition += lineHeight * 0.5;

    // Log Entries
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    filteredLogs.forEach((entry, index) => {
      checkNewPage(lineHeight * 1.5);
      
      const timestampStr = format(entry.timestamp, 'yyyy-MM-dd HH:mm:ss');
      const statusStr = entry.status || 'Unknown';
      const responseTimeStr = entry.responseTime !== null && entry.responseTime !== undefined 
        ? `${Math.round(entry.responseTime)}ms` 
        : 'N/A';
      
      // Calculate duration to next entry
      let durationStr = '-';
      if (index < filteredLogs.length - 1) {
        const nextEntry = filteredLogs[index + 1];
        const duration = (nextEntry.timestamp - entry.timestamp) / 1000; // in seconds
        if (duration < 60) {
          durationStr = `${Math.round(duration)}s`;
        } else if (duration < 3600) {
          durationStr = `${Math.round(duration / 60)}m ${Math.round(duration % 60)}s`;
        } else {
          const hours = Math.floor(duration / 3600);
          const minutes = Math.floor((duration % 3600) / 60);
          durationStr = `${hours}h ${minutes}m`;
        }
      }

      // Set color based on status
      if (statusStr === 'Critical') {
        doc.setTextColor(220, 53, 69); // Red
      } else if (statusStr === 'Warning') {
        doc.setTextColor(255, 193, 7); // Yellow/Orange
      } else {
        doc.setTextColor(40, 167, 69); // Green
      }

      // Truncate long strings if needed
      const maxWidth = col4 - col1 - 5;
      doc.text(timestampStr.substring(0, 19), col1, yPosition);
      doc.text(statusStr, col2, yPosition);
      doc.text(responseTimeStr, col3, yPosition);
      doc.text(durationStr, col4, yPosition);
      
      // Reset color to black
      doc.setTextColor(0, 0, 0);
      
      yPosition += lineHeight;
    });

    // Add logo to all pages (no cover page for this report)
    await addLogoToAllPages(doc, pageWidth, pageHeight, false);

    // Footer
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${i} of ${totalPages} - Generated by Web Monitor`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Generate filename
    const siteName = (site.name || 'site').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const filename = `uptime-log-${siteName}-${getRangeDisplayName(selectedRange).toLowerCase()}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    
    // Save PDF
    doc.save(filename);
  };

  // Determine status display
  const isHealthy = site.status === 'Healthy';
  const statusIcon = isHealthy ? '✓' : '✗';
  const statusColor = isHealthy ? '#28a745' : '#dc3545';

  // Get uptime and downtime percentages
  const uptimePercentage = uptimeData ? uptimeData.uptimePercentage : null;
  const downtimePercentage = uptimeData ? uptimeData.downtimePercentage : null;

  return (
    <div className="uptime-container">
      <div className="uptime-statistics">
        <div className="uptime-statistics-header">
          <h2>Uptime Statistics</h2>
          <button 
            onClick={handleRefreshUptime} 
            disabled={isRefreshing}
            className="refresh-uptime-button"
          >
            <span className="refresh-icon">↻</span>
            Refresh Uptime
          </button>
        </div>
        
        <div className="uptime-stats-grid">
          <div className="uptime-stat-item">
            <div className="stat-icon" style={{ color: statusColor }}>
              {statusIcon}
            </div>
            <div className="stat-content">
              <div className="stat-label">Uptime Status</div>
              <div className="stat-value" style={{ color: statusColor }}>
                {site.status || 'Unknown'}
              </div>
            </div>
          </div>

          <div className="uptime-stat-item">
            <div className="stat-icon" style={{ color: '#ff9800' }}>
              ⚠
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Downtime</div>
              <div className="stat-value">
                {uptimeData ? formatTime(uptimeData.downtimeSeconds) : 'N/A'}
              </div>
              <div className="stat-subtitle">{getRangeDisplayName(selectedRange)}</div>
            </div>
          </div>

          <div className="uptime-stat-item">
            <div className="stat-icon" style={{ color: '#2196f3' }}>
              ↑
            </div>
            <div className="stat-content">
              <div className="stat-label">Avg. Response Time</div>
              <div className="stat-value">{formatResponseTime(site.averageResponseTime)}</div>
              <div className="stat-subtitle">Last 100 checks</div>
            </div>
          </div>
        </div>
      </div>

      <div className="uptime-history">
        <div className="uptime-history-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <h2>Uptime History</h2>
            <button
              onClick={handleDownloadPDF}
              className="download-pdf-button"
              title="Download Uptime & Downtime Log as PDF"
              disabled={!uptimeData || !uptimeData.data || uptimeData.data.length === 0}
            >
              <span style={{ marginRight: '6px' }}>📥</span>
              Download PDF
            </button>
          </div>
          <div className="range-filter" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={selectedRange}
              onChange={handleRangeChange}
              className="range-filter-dropdown"
            >
              <option value="30minutes">Last 30 minutes</option>
              <option value="24hours">Last 24 hours</option>
              <option value="month">Last month</option>
              <option value="3months">Last 3 months</option>
              <option value="custom">Custom</option>
            </select>
            {selectedRange === 'custom' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label htmlFor="date-picker" style={{ fontSize: '13px', color: '#646970', whiteSpace: 'nowrap' }}>
                  Select date:
                </label>
                <input
                  id="date-picker"
                  type="date"
                  value={selectedDate || ''}
                  onChange={handleDateChange}
                  className="date-picker-input"
                  max={format(new Date(), 'yyyy-MM-dd')} // Can't select future dates
                />
                {selectedDate && (
                  <button
                    onClick={() => {
                      setSelectedDate(null);
                    }}
                    className="clear-date-button"
                    title="Clear date filter"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="chart-container">
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <p>Loading uptime data...</p>
            </div>
          ) : chartData && chartData.labels && chartData.labels.length > 0 ? (
            <Line ref={chartRef} data={chartData} options={getChartOptions()} />
          ) : uptimeData && uptimeData.data && uptimeData.data.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '16px' }}>
              <p>
                {selectedDate 
                  ? `No monitoring data available for ${format(new Date(selectedDate + 'T00:00:00'), 'yyyy-MM-dd')}.`
                  : `No data available for ${getRangeDisplayName(selectedRange).toLowerCase()}.`
                }
              </p>
              <button 
                onClick={handleRefreshUptime}
                className="refresh-uptime-button"
                style={{ marginTop: '8px' }}
              >
                <span className="refresh-icon">↻</span>
                Run Uptime Check
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '16px' }}>
              <p>
                {selectedDate 
                  ? `Not enough data to display chart for ${format(new Date(selectedDate + 'T00:00:00'), 'yyyy-MM-dd')}.`
                  : `Not enough data to display chart for ${getRangeDisplayName(selectedRange).toLowerCase()}.`
                }
              </p>
              <button 
                onClick={handleRefreshUptime}
                className="refresh-uptime-button"
                style={{ marginTop: '8px' }}
              >
                <span className="refresh-icon">↻</span>
                Run Uptime Check
              </button>
            </div>
          )}
        </div>
        
        <div className="chart-legend">
          <div className="legend-item">
            <span className="legend-dot operational"></span>
            <span>Operational ({uptimePercentage !== null ? `${uptimePercentage}%` : 'N/A'})</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot downtime"></span>
            <span>Downtime ({downtimePercentage !== null ? `${downtimePercentage}%` : 'N/A'})</span>
          </div>
        </div>
      </div>

      {/* Historical Uptime Calendar */}
      <div className="historical-uptime">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2>Historical uptime</h2>
          <button
            onClick={fetchDailyUptime}
            className="refresh-uptime-button"
            style={{ fontSize: '11px', padding: '4px 10px' }}
            title="Refresh historical uptime data"
          >
            ↻ Refresh
          </button>
        </div>
        {loadingDailyUptime ? (
          <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px' }}>Loading historical uptime data...</div>
        ) : dailyUptime !== null && dailyUptime.length > 0 ? (
          <HistoricalUptimeCalendar dailyUptime={dailyUptime} />
        ) : (
          <div style={{ padding: '12px', textAlign: 'center', color: '#646970', fontSize: '13px' }}>
            {dailyUptime === null 
              ? 'Loading historical uptime data...' 
              : `No historical uptime data available. Data will appear after the site has been monitored for at least one day.
                 ${site?.statusHistory?.length ? `(Site has ${site.statusHistory.length} status history entries)` : '(No status history yet)'}`}
          </div>
        )}
      </div>

      {/* Incidents History Table */}
      <div className="incidents-history">
        <div className="incidents-history-header">
          <h2>Incidents history</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={fetchIncidents}
              className="refresh-uptime-button"
              style={{ fontSize: '12px', padding: '6px 12px' }}
              title="Refresh incidents data"
            >
              ↻ Refresh
            </button>
            {incidents && incidents.length > 0 && (
              <button
                onClick={() => {
                  // Download incidents as CSV
                  const csvContent = [
                    ['DATE & TIME', 'DOWNTIME', 'RESPONSE SUMMARY', 'NOTES'],
                    ...incidents.map(incident => [
                      format(parseISO(incident.dateTime), 'MMMM d, yyyy, h:mm a \'UTC\''),
                      `${incident.downtime} mins`,
                      incident.responseSummary,
                      incident.notes || ''
                    ])
                  ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
                  
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `incidents-${site.name || 'site'}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="download-incidents-button"
              >
                Download incidents
              </button>
            )}
          </div>
        </div>
        {loadingIncidents ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading incidents...</div>
        ) : incidents !== null && incidents.length > 0 ? (
          <IncidentsHistoryTable incidents={incidents} siteId={site?._id} onNotesSaved={fetchIncidents} />
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#646970' }}>
            {incidents === null 
              ? 'Loading incidents...' 
              : `No incidents recorded. Incidents will appear here when the site experiences downtime.
                 ${site?.statusHistory?.length ? `(Site has ${site.statusHistory.length} status history entries)` : '(No status history yet)'}`}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to format time (moved outside component for reuse)
const formatTime = (seconds) => {
  if (seconds === undefined || seconds === null) return 'N/A';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.round(seconds % 60);
  
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);
  
  return parts.join(' ');
};

// Historical Uptime Calendar Component
const HistoricalUptimeCalendar = ({ dailyUptime }) => {
  const [hoveredDay, setHoveredDay] = useState(null);
  
  // Group daily uptime by month
  const monthlyData = {};
  dailyUptime.forEach(day => {
    const date = parseISO(day.date);
    const monthKey = format(date, 'yyyy-MM');
    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = [];
    }
    monthlyData[monthKey].push(day);
  });

  // Get last 3 months
  const months = Object.keys(monthlyData)
    .sort()
    .slice(-3)
    .map(key => ({
      key,
      monthName: format(parseISO(`${key}-01`), 'MMMM'),
      year: format(parseISO(`${key}-01`), 'yyyy'),
      days: monthlyData[key]
    }));

  const getDayStatus = (dateStr) => {
    const day = dailyUptime.find(d => d.date === dateStr);
    if (!day) return 'no-data';
    return day.hasDowntime ? 'down' : 'up';
  };

  const getDayData = (dateStr) => {
    return dailyUptime.find(d => d.date === dateStr);
  };

  const renderMonth = (month) => {
    const monthStart = startOfMonth(parseISO(`${month.key}-01`));
    const monthEnd = endOfMonth(monthStart);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    const today = new Date();
    
    return (
      <div key={month.key} className="historical-uptime-month">
        <div className="historical-uptime-month-header">
          <span className="month-name">{month.monthName}</span>
          <span className="month-year">{month.year}</span>
        </div>
        <div className="historical-uptime-calendar-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="calendar-weekday">{day}</div>
          ))}
          {days.map(day => {
            const dayStr = format(day, 'yyyy-MM-dd');
            const dayData = getDayData(dayStr);
            const status = getDayStatus(dayStr);
            const isToday = isSameDay(day, today);
            const isFuture = day > today;
            const isOtherMonth = !isSameMonth(day, monthStart);
            
            return (
              <div
                key={dayStr}
                className={`calendar-day ${status} ${isOtherMonth ? 'other-month' : ''} ${isFuture ? 'future' : ''} ${isToday ? 'today' : ''}`}
                onMouseEnter={() => dayData && !isFuture && !isOtherMonth ? setHoveredDay(dayStr) : null}
                onMouseLeave={() => setHoveredDay(null)}
                title={dayData ? `${format(day, 'MMMM d')}: ${dayData.uptimePercent.toFixed(2)}% UP` : ''}
              >
                <div className="calendar-day-dot"></div>
                {hoveredDay === dayStr && dayData && (
                  <div className="calendar-day-tooltip">
                    <div className="tooltip-date">{format(day, 'MMMM d')}</div>
                    <div className="tooltip-uptime">{dayData.uptimePercent.toFixed(2)}% UP</div>
                    <div className="tooltip-downtime">Downtime: {dayData.downtime > 0 ? formatTime(dayData.downtime) : '--'}</div>
                    <div className="tooltip-incidents">Incidents: {dayData.incidents}</div>
                    <div className="tooltip-response">Avg. response time: {dayData.avgResponseTime ? `${dayData.avgResponseTime} ms` : 'N/A'}</div>
                    <div className="tooltip-apdex">Apdex: {dayData.apdex.toFixed(2)}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="historical-uptime-calendar">
      {months.map(renderMonth)}
      <div className="historical-uptime-legend">
        <div className="legend-item">
          <span className="legend-dot up"></span>
          <span>Uptime</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot down"></span>
          <span>Downtime</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot no-data"></span>
          <span>No data</span>
        </div>
      </div>
    </div>
  );
};

// Incidents History Table Component
const IncidentsHistoryTable = ({ incidents, siteId, onNotesSaved }) => {
  const [editingNotes, setEditingNotes] = useState({});
  const [notes, setNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Initialize notes from incidents when component mounts or incidents change
  useEffect(() => {
    const notesMap = {};
    incidents.forEach((incident, index) => {
      if (incident.notes) {
        notesMap[index] = incident.notes;
      }
    });
    setNotes(notesMap);
  }, [incidents]);

  const totalPages = Math.ceil(incidents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentIncidents = incidents.slice(startIndex, endIndex);

  const handleNotesChange = (incidentIndex, value) => {
    setNotes(prev => ({
      ...prev,
      [incidentIndex]: value
    }));
  };

  const handleSaveNotes = async (incidentIndex) => {
    const incident = incidents[incidentIndex];
    if (!incident || !siteId) {
      console.error('Cannot save notes: missing incident or siteId');
      return;
    }

    const noteText = notes[incidentIndex] || '';
    setSavingNotes(prev => ({ ...prev, [incidentIndex]: true }));

    try {
      // Use POST instead of PUT to avoid URL encoding issues with dateTime
      await axios.post(`/api/sites/${siteId}/incidents/notes`, {
        notes: noteText,
        dateTime: incident.dateTime
      });

      // Update local state
      setEditingNotes(prev => {
        const newState = { ...prev };
        delete newState[incidentIndex];
        return newState;
      });

      // Refresh incidents to get updated data
      if (onNotesSaved) {
        onNotesSaved();
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes. Please try again.');
    } finally {
      setSavingNotes(prev => {
        const newState = { ...prev };
        delete newState[incidentIndex];
        return newState;
      });
    }
  };

  const handleEditNotes = (incidentIndex) => {
    const incident = incidents[incidentIndex];
    // Initialize notes with existing value if available
    if (!notes[incidentIndex] && incident.notes) {
      setNotes(prev => ({
        ...prev,
        [incidentIndex]: incident.notes
      }));
    }
    setEditingNotes(prev => ({ ...prev, [incidentIndex]: true }));
  };

  const handleCancelEdit = (incidentIndex) => {
    const incident = incidents[incidentIndex];
    // Reset to original notes value
    setNotes(prev => {
      const newState = { ...prev };
      if (incident.notes) {
        newState[incidentIndex] = incident.notes;
      } else {
        delete newState[incidentIndex];
      }
      return newState;
    });
    setEditingNotes(prev => {
      const newState = { ...prev };
      delete newState[incidentIndex];
      return newState;
    });
  };

  const handleViewAnalysis = (incident) => {
    // This would open a detailed analysis view
    alert(`Viewing analysis for incident on ${format(parseISO(incident.dateTime), 'MMMM d, yyyy')}`);
  };

  return (
    <div className="incidents-table-container">
      <table className="incidents-table">
        <thead>
          <tr>
            <th>DATE & TIME</th>
            <th>DOWNTIME</th>
            <th>RESPONSE SUMMARY</th>
            <th>NOTES</th>
          </tr>
        </thead>
        <tbody>
          {currentIncidents.map((incident, index) => {
            const globalIndex = startIndex + index;
            const isEditing = editingNotes[globalIndex];
            
            return (
              <tr key={globalIndex}>
                <td>{format(parseISO(incident.dateTime), 'MMMM d, yyyy, h:mm a \'UTC\'')}</td>
                <td>{incident.downtime} mins</td>
                <td>{incident.responseSummary}</td>
                <td>
                  {isEditing ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        value={notes[globalIndex] || ''}
                        onChange={(e) => handleNotesChange(globalIndex, e.target.value)}
                        className="notes-input"
                        placeholder="Add notes"
                        autoFocus
                        style={{ flex: 1, minWidth: '200px' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveNotes(globalIndex);
                          } else if (e.key === 'Escape') {
                            handleCancelEdit(globalIndex);
                          }
                        }}
                      />
                      <button
                        onClick={() => handleSaveNotes(globalIndex)}
                        className="save-notes-button"
                        disabled={savingNotes[globalIndex]}
                        style={{ opacity: savingNotes[globalIndex] ? 0.6 : 1 }}
                      >
                        {savingNotes[globalIndex] ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => handleCancelEdit(globalIndex)}
                        className="cancel-notes-button"
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span
                      className="add-notes-link"
                      onClick={() => handleEditNotes(globalIndex)}
                      style={{ cursor: 'pointer' }}
                      title={incident.notes ? 'Click to edit notes' : 'Click to add notes'}
                    >
                      {incident.notes || 'Add notes'}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="incidents-pagination">
          <span className="pagination-info">
            {startIndex + 1} - {Math.min(endIndex, incidents.length)} of {incidents.length}
          </span>
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Uptime;
